import {Injectable} from '@angular/core';
import {map, Observable, of, switchMap, take} from 'rxjs';
import {HttpClient, HttpStatusCode} from '@angular/common/http';
import {
  HostMemberRegistrationRequest,
  MemberDTO,
  MemberRole,
  ValidateMemberJoinRequest
} from '@domain/member/member.dto';
import {ApiUtil} from '@shared/util/apiUtil';
import {HostDTO} from '@domain/host/host.dto';
import {catchError} from 'rxjs/operators';
import {AuthService} from '@core/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class MemberApi {

  protected readonly apiPrefix = 'member';

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) { }

  public getAllForMe(): Observable<MemberDTO[] | null> {
    return this.http.get<MemberDTO[]>(ApiUtil.buildUrl(this.getPath())).pipe(
      take(1)
    );
  }

  public getHostPreview(memberId: string): Observable<HostDTO | null> {
    return this.http.get<HostDTO | null>(ApiUtil.buildUrl(this.getPath(memberId), '/host')).pipe(
      take(1)
    );
  }

  public registerHost(registration: HostMemberRegistrationRequest): Observable<HostDTO | null> {
    return this.http.post<HostDTO>(ApiUtil.buildUrl(this.getPath(), '/register'), registration);
  }

  public validateJoin(validateRequest: ValidateMemberJoinRequest): Observable<boolean> {
    return this.http
      .post<void>(
        ApiUtil.buildUrl(this.getPath(), '/join'),
        validateRequest,
        {observe: 'response'}
      )
      .pipe(
        map(response => response?.status === HttpStatusCode.NoContent),
        switchMap(validated => validated ? this.authService.refreshAuthenticatedUser().pipe(map(user => !!user)) : of(false)),
        catchError(_err => of(false)),
        take(1)
      );
  }

  public delete(memberId: string): Observable<void> {
    return this.http.delete<void>(ApiUtil.buildUrl(this.getPath(memberId))).pipe(
      take(1)
    );
  }

  public update(memberId: string, roles: MemberRole[]): Observable<MemberDTO> {
    const updateRequest = {
      roles: roles
    };
    return this.http.patch<MemberDTO>(ApiUtil.buildUrl(this.getPath(memberId)), updateRequest).pipe(
      take(1)
    );
  }

  private getPath(memberId?: string | null): string {
    return memberId
      ? `member/${memberId}`
      : `member`;
  }
}
